// Procedural level generation. Pure function of (roomSeed, zoneId): the server and all
// clients run this independently and get byte-identical maps, so maps never cross the wire.

import { HUB_ZONE, ARENA_ZONE, RealmDef, parseZone, zoneId } from './realms';
import { hashStr, mixSeed, RNG } from './rng';
import { MapObject, MapPack, T_BLOCK, T_FLOOR, T_LIQUID, T_VOID, T_WALL, Tier, ZoneMap } from './types';

const LOBE_DIRS: readonly [number, number][] = [
  [1, 0], [0.92, 0.38], [0.71, 0.71], [0.38, 0.92], [0, 1], [-0.38, 0.92], [-0.71, 0.71], [-0.92, 0.38],
  [-1, 0], [-0.92, -0.38], [-0.71, -0.71], [-0.38, -0.92], [0, -1], [0.38, -0.92], [0.71, -0.71], [0.92, -0.38],
];
const BOSS_PILLARS: readonly [number, number][] = [[7, 2], [2, 7], [-5, 5], [-7, -2], [-2, -7], [5, -5]];

class Grid {
  tiles: Uint8Array;
  constructor(public w: number, public h: number, fill: number) {
    this.tiles = new Uint8Array(w * h).fill(fill);
  }
  get(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return T_WALL;
    return this.tiles[y * this.w + x];
  }
  set(x: number, y: number, t: number): void {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    this.tiles[y * this.w + x] = t;
  }
  rect(x0: number, y0: number, w: number, h: number, t: number): void {
    for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) this.set(x, y, t);
  }
  disc(cx: number, cy: number, r: number, t: number): void {
    const r2 = r * r;
    for (let y = cy - r; y <= cy + r; y++) {
      for (let x = cx - r; x <= cx + r; x++) {
        const dx = x - cx;
        const dy = y - cy;
        if (dx * dx + dy * dy <= r2) this.set(x, y, t);
      }
    }
  }
  border(n: number, t: number): void {
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (x < n || y < n || x >= this.w - n || y >= this.h - n) this.tiles[y * this.w + x] = t;
      }
    }
  }
}

function bfs(g: Grid, sx: number, sy: number): Int32Array {
  const dist = new Int32Array(g.w * g.h).fill(-1);
  const queue = new Int32Array(g.w * g.h);
  let head = 0;
  let tail = 0;
  const start = sy * g.w + sx;
  dist[start] = 0;
  queue[tail++] = start;
  while (head < tail) {
    const cur = queue[head++];
    const cx = cur % g.w;
    const cy = (cur / g.w) | 0;
    const d = dist[cur];
    if (cx > 0 && g.tiles[cur - 1] === T_FLOOR && dist[cur - 1] < 0) { dist[cur - 1] = d + 1; queue[tail++] = cur - 1; }
    if (cx < g.w - 1 && g.tiles[cur + 1] === T_FLOOR && dist[cur + 1] < 0) { dist[cur + 1] = d + 1; queue[tail++] = cur + 1; }
    if (cy > 0 && g.tiles[cur - g.w] === T_FLOOR && dist[cur - g.w] < 0) { dist[cur - g.w] = d + 1; queue[tail++] = cur - g.w; }
    if (cy < g.h - 1 && g.tiles[cur + g.w] === T_FLOOR && dist[cur + g.w] < 0) { dist[cur + g.w] = d + 1; queue[tail++] = cur + g.w; }
  }
  return dist;
}

/** Find connected floor regions; keep/join them so that every floor tile is reachable. */
function connectRegions(g: Grid, rng: RNG, carveTile: number = T_FLOOR, minKeep = 12): void {
  const labels = new Int32Array(g.w * g.h).fill(-1);
  const regions: number[][] = [];
  const stack: number[] = [];
  for (let i = 0; i < g.tiles.length; i++) {
    if (g.tiles[i] !== T_FLOOR || labels[i] >= 0) continue;
    const id = regions.length;
    const cells: number[] = [];
    labels[i] = id;
    stack.push(i);
    while (stack.length) {
      const cur = stack.pop()!;
      cells.push(cur);
      const cx = cur % g.w;
      const cy = (cur / g.w) | 0;
      if (cx > 0 && g.tiles[cur - 1] === T_FLOOR && labels[cur - 1] < 0) { labels[cur - 1] = id; stack.push(cur - 1); }
      if (cx < g.w - 1 && g.tiles[cur + 1] === T_FLOOR && labels[cur + 1] < 0) { labels[cur + 1] = id; stack.push(cur + 1); }
      if (cy > 0 && g.tiles[cur - g.w] === T_FLOOR && labels[cur - g.w] < 0) { labels[cur - g.w] = id; stack.push(cur - g.w); }
      if (cy < g.h - 1 && g.tiles[cur + g.w] === T_FLOOR && labels[cur + g.w] < 0) { labels[cur + g.w] = id; stack.push(cur + g.w); }
    }
    regions.push(cells);
  }
  if (regions.length <= 1) return;
  regions.sort((a, b) => b.length - a.length);
  const main = regions[0];
  for (let r = 1; r < regions.length; r++) {
    const cells = regions[r];
    if (cells.length < minKeep) {
      for (const c of cells) g.tiles[c] = carveTile === T_FLOOR ? T_WALL : T_VOID;
      continue;
    }
    // Sample a closest pair and carve a corridor.
    let best = Infinity;
    let a = cells[0];
    let b = main[0];
    for (let s = 0; s < 80; s++) {
      const ca = cells[rng.int(0, cells.length - 1)];
      const cb = main[rng.int(0, main.length - 1)];
      const d = Math.abs((ca % g.w) - (cb % g.w)) + Math.abs(((ca / g.w) | 0) - ((cb / g.w) | 0));
      if (d < best) {
        best = d;
        a = ca;
        b = cb;
      }
    }
    carveCorridor(g, a % g.w, (a / g.w) | 0, b % g.w, (b / g.w) | 0, 2, rng);
    main.push(...cells);
  }
}

function carveCorridor(g: Grid, x0: number, y0: number, x1: number, y1: number, width: number, rng: RNG): void {
  const horizontalFirst = rng.chance(0.5);
  const carveH = (y: number, a: number, b: number) => {
    const s = Math.min(a, b);
    const e = Math.max(a, b);
    for (let x = s; x <= e; x++) for (let w = 0; w < width; w++) if (x > 1 && x < g.w - 2 && y + w > 1 && y + w < g.h - 2) g.set(x, y + w, T_FLOOR);
  };
  const carveV = (x: number, a: number, b: number) => {
    const s = Math.min(a, b);
    const e = Math.max(a, b);
    for (let y = s; y <= e; y++) for (let w = 0; w < width; w++) if (y > 1 && y < g.h - 2 && x + w > 1 && x + w < g.w - 2) g.set(x + w, y, T_FLOOR);
  };
  if (horizontalFirst) {
    carveH(y0, x0, x1);
    carveV(x1, y0, y1);
  } else {
    carveV(x0, y0, y1);
    carveH(y1, x0, x1);
  }
}

function floorCells(g: Grid): number[] {
  const out: number[] = [];
  for (let i = 0; i < g.tiles.length; i++) if (g.tiles[i] === T_FLOOR) out.push(i);
  return out;
}

/** Picks the pair of far-apart floor tiles to serve as entrance & exit. */
function farPoints(g: Grid, rng: RNG): { a: [number, number]; b: [number, number] } {
  const cells = floorCells(g);
  const c0 = cells[rng.int(0, cells.length - 1)];
  const d0 = bfs(g, c0 % g.w, (c0 / g.w) | 0);
  let f = c0;
  let fd = 0;
  for (let i = 0; i < d0.length; i++) if (d0[i] > fd) { fd = d0[i]; f = i; }
  const d1 = bfs(g, f % g.w, (f / g.w) | 0);
  let e = f;
  let ed = 0;
  for (let i = 0; i < d1.length; i++) if (d1[i] > ed) { ed = d1[i]; e = i; }
  return { a: [f % g.w, (f / g.w) | 0], b: [e % g.w, (e / g.w) | 0] };
}

// ─── Generators ───

function genCave(g: Grid, rng: RNG, density: number): void {
  const { w, h } = g;
  let cur = new Uint8Array(w * h);
  for (let i = 0; i < cur.length; i++) cur[i] = rng.chance(density) ? 1 : 0;
  for (let it = 0; it < 4; it++) {
    const nxt = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) n++;
            else n += cur[ny * w + nx];
          }
        }
        nxt[y * w + x] = n >= 5 ? 1 : 0;
      }
    }
    cur = nxt;
  }
  for (let i = 0; i < cur.length; i++) g.tiles[i] = cur[i] ? T_WALL : T_FLOOR;
  g.border(2, T_WALL);
  connectRegions(g, rng, T_FLOOR, 30);
}

interface Room { x: number; y: number; w: number; h: number; cx: number; cy: number }

function genRooms(g: Grid, rng: RNG, realm: RealmDef): Room[] {
  const rooms: Room[] = [];
  const tries = 220;
  const maxRooms = Math.floor((g.w * g.h) / 260);
  for (let t = 0; t < tries && rooms.length < maxRooms; t++) {
    const rw = rng.int(8, 17);
    const rh = rng.int(8, 15);
    const rx = rng.int(3, g.w - rw - 4);
    const ry = rng.int(3, g.h - rh - 4);
    let overlap = false;
    for (const r of rooms) {
      if (rx < r.x + r.w + 4 && rx + rw + 4 > r.x && ry < r.y + r.h + 4 && ry + rh + 4 > r.y) {
        overlap = true;
        break;
      }
    }
    if (overlap) continue;
    const room = { x: rx, y: ry, w: rw, h: rh, cx: rx + (rw >> 1), cy: ry + (rh >> 1) };
    rooms.push(room);
    g.rect(rx, ry, rw, rh, T_FLOOR);
  }
  // Minimum spanning tree over room centers, plus a few loops so that levels aren't linear.
  const connected = new Set<number>([0]);
  const edges: [number, number][] = [];
  while (connected.size < rooms.length) {
    let bd = Infinity;
    let ba = 0;
    let bb = 0;
    for (const a of connected) {
      for (let b = 0; b < rooms.length; b++) {
        if (connected.has(b)) continue;
        const d = Math.abs(rooms[a].cx - rooms[b].cx) + Math.abs(rooms[a].cy - rooms[b].cy);
        if (d < bd) {
          bd = d;
          ba = a;
          bb = b;
        }
      }
    }
    connected.add(bb);
    edges.push([ba, bb]);
  }
  for (let i = 0; i < Math.floor(rooms.length * 0.25); i++) {
    edges.push([rng.int(0, rooms.length - 1), rng.int(0, rooms.length - 1)]);
  }
  for (const [a, b] of edges) {
    if (a === b) continue;
    carveCorridor(g, rooms[a].cx, rooms[a].cy, rooms[b].cx, rooms[b].cy, rng.chance(0.5) ? 3 : 2, rng);
  }
  // Room furniture: pillars, pits.
  for (const r of rooms) {
    const roll = rng.next();
    if (r.w >= 11 && r.h >= 10 && roll < 0.4) {
      // Rows of pillars
      for (let y = r.y + 2; y < r.y + r.h - 2; y += 3) {
        for (let x = r.x + 2; x < r.x + r.w - 2; x += 4) {
          if (Math.abs(x - r.cx) > 1 && Math.abs(y - r.cy) > 1) g.set(x, y, T_BLOCK);
        }
      }
    } else if (r.w >= 11 && r.h >= 10 && roll < 0.65) {
      // Central pit of liquid
      const pw = r.w - 7;
      const ph = r.h - 7;
      const px = r.x + 4;
      const py = r.y + 4;
      if (pw > 0 && ph > 0) g.rect(px, py, pw, ph, realm.liquid === 'void' ? T_VOID : T_LIQUID);
    } else if (roll < 0.85) {
      // Scattered props
      const n = rng.int(2, 5);
      for (let i = 0; i < n; i++) {
        const x = rng.int(r.x + 1, r.x + r.w - 2);
        const y = rng.int(r.y + 1, r.y + r.h - 2);
        if (Math.abs(x - r.cx) > 2 || Math.abs(y - r.cy) > 2) g.set(x, y, T_BLOCK);
      }
    }
  }
  g.border(2, T_WALL);
  connectRegions(g, rng, T_FLOOR, 12);
  return rooms;
}

function genOpen(g: Grid, rng: RNG): void {
  // Low-density cave base, broken up with ruined rectangular structures.
  genCave(g, rng, 0.34);
  const n = Math.floor((g.w * g.h) / 500);
  for (let i = 0; i < n; i++) {
    const rw = rng.int(5, 11);
    const rh = rng.int(4, 9);
    const rx = rng.int(4, g.w - rw - 5);
    const ry = rng.int(4, g.h - rh - 5);
    // Walls with gaps: a ruined shell.
    for (let x = rx; x < rx + rw; x++) {
      if (rng.chance(0.72)) g.set(x, ry, T_WALL);
      if (rng.chance(0.72)) g.set(x, ry + rh - 1, T_WALL);
    }
    for (let y = ry; y < ry + rh; y++) {
      if (rng.chance(0.72)) g.set(rx, y, T_WALL);
      if (rng.chance(0.72)) g.set(rx + rw - 1, y, T_WALL);
    }
    g.rect(rx + 1, ry + 1, rw - 2, rh - 2, T_FLOOR);
  }
  // Blocks (pillars, cacti etc.) scattered around.
  const blocks = Math.floor((g.w * g.h) / 70);
  for (let i = 0; i < blocks; i++) {
    const x = rng.int(3, g.w - 4);
    const y = rng.int(3, g.h - 4);
    if (g.get(x, y) === T_FLOOR) g.set(x, y, T_BLOCK);
  }
  g.border(2, T_WALL);
  connectRegions(g, rng, T_FLOOR, 16);
}

function genCity(g: Grid, rng: RNG): void {
  g.tiles.fill(T_FLOOR);
  const road = 5;
  let y = 3;
  while (y < g.h - 8) {
    const bh = rng.int(6, 12);
    let x = 3;
    while (x < g.w - 8) {
      const bw = rng.int(6, 13);
      const ex = Math.min(x + bw, g.w - 3);
      const ey = Math.min(y + bh, g.h - 3);
      const roll = rng.next();
      if (roll < 0.62) {
        g.rect(x, y, ex - x, ey - y, T_WALL);
        // Some buildings have alcoves or cut corners so that blocks aren't all rectangles.
        if (rng.chance(0.4)) {
          const cw = Math.min(3, ex - x - 2);
          g.rect(x, y, cw, 2, T_FLOOR);
        }
        if (rng.chance(0.4)) {
          const cw = Math.min(3, ex - x - 2);
          g.rect(ex - cw, ey - 2, cw, 2, T_FLOOR);
        }
      } else if (roll < 0.76) {
        // Toxic canal plaza
        g.rect(x + 1, y + 1, Math.max(1, ex - x - 2), Math.max(1, ey - y - 2), T_LIQUID);
      } else if (roll < 0.92) {
        // Plaza with pillars / vending machines
        for (let py = y + 1; py < ey - 1; py += 3) {
          for (let px = x + 1; px < ex - 1; px += 3) {
            if (rng.chance(0.55)) g.set(px, py, T_BLOCK);
          }
        }
      }
      x = ex + road;
    }
    y += bh + road;
  }
  // Roadside clutter
  const n = Math.floor((g.w * g.h) / 120);
  for (let i = 0; i < n; i++) {
    const px = rng.int(3, g.w - 4);
    const py = rng.int(3, g.h - 4);
    if (g.get(px, py) === T_FLOOR && g.get(px - 1, py) === T_WALL) g.set(px, py, T_BLOCK);
  }
  g.border(2, T_WALL);
  connectRegions(g, rng, T_FLOOR, 16);
}

function genIslands(g: Grid, rng: RNG): void {
  g.tiles.fill(T_VOID);
  const islands: { x: number; y: number; r: number }[] = [];
  const target = Math.floor((g.w * g.h) / 330);
  for (let t = 0; t < 400 && islands.length < target; t++) {
    const r = rng.int(5, 10);
    const x = rng.int(r + 3, g.w - r - 4);
    const y = rng.int(r + 3, g.h - r - 4);
    let ok = true;
    for (const i of islands) {
      const ddx = i.x - x;
      const ddy = i.y - y;
      const minD = i.r + r + 3;
      if (ddx * ddx + ddy * ddy < minD * minD) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    islands.push({ x, y, r });
    // Lumpy island: union of a few discs.
    g.disc(x, y, r, T_FLOOR);
    const lobes = rng.int(2, 5);
    for (let l = 0; l < lobes; l++) {
      // Integer direction table: trig results can differ between JS engines.
      const [ox, oy] = rng.pick(LOBE_DIRS);
      const d = (r * 7) / 10;
      g.disc(Math.round(x + ox * d), Math.round(y + oy * d), rng.int(2, Math.max(3, r - 3)), T_FLOOR);
    }
    // Flesh pillars
    const n = rng.int(0, 4);
    for (let p = 0; p < n; p++) {
      const px = rng.int(x - r + 2, x + r - 2);
      const py = rng.int(y - r + 2, y + r - 2);
      const pdx = px - x;
      const pdy = py - y;
      if (g.get(px, py) === T_FLOOR && pdx * pdx + pdy * pdy > 4) g.set(px, py, T_BLOCK);
    }
  }
  // MST of bridges.
  const connected = new Set<number>([0]);
  while (connected.size < islands.length) {
    let bd = Infinity;
    let ba = 0;
    let bb = 0;
    for (const a of connected) {
      for (let b = 0; b < islands.length; b++) {
        if (connected.has(b)) continue;
        const ix = islands[a].x - islands[b].x;
        const iy = islands[a].y - islands[b].y;
        const d = ix * ix + iy * iy;
        if (d < bd) {
          bd = d;
          ba = a;
          bb = b;
        }
      }
    }
    connected.add(bb);
    carveBridge(g, islands[ba], islands[bb], rng);
  }
  g.border(2, T_VOID);
  connectRegions(g, rng, T_VOID, 12);
}

function carveBridge(g: Grid, a: { x: number; y: number }, b: { x: number; y: number }, rng: RNG): void {
  carveCorridor(g, a.x, a.y, b.x, b.y, rng.chance(0.5) ? 3 : 2, rng);
}

function addPools(g: Grid, rng: RNG, count: number, kind: number): void {
  const cells = floorCells(g);
  for (let i = 0; i < count; i++) {
    const c = cells[rng.int(0, cells.length - 1)];
    const cx = c % g.w;
    const cy = (c / g.w) | 0;
    const r = rng.int(2, 4);
    for (let y = cy - r; y <= cy + r; y++) {
      for (let x = cx - r; x <= cx + r; x++) {
        const dx = x - cx;
        const dy = y - cy;
        if (dx * dx + dy * dy <= r * r && g.get(x, y) === T_FLOOR) g.set(x, y, kind);
      }
    }
  }
}

function scatterBlocks(g: Grid, rng: RNG, count: number): void {
  for (let i = 0; i < count; i++) {
    const x = rng.int(3, g.w - 4);
    const y = rng.int(3, g.h - 4);
    if (g.get(x, y) !== T_FLOOR) continue;
    // Don't choke corridors: only place in open space.
    let open = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (g.get(x + dx, y + dy) === T_FLOOR) open++;
    if (open === 9) g.set(x, y, T_BLOCK);
  }
}

function clearAround(g: Grid, x: number, y: number, r: number): void {
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 2 && ny >= 2 && nx < g.w - 2 && ny < g.h - 2) g.set(nx, ny, T_FLOOR);
    }
  }
}

function finish(id: string, g: Grid, spawn: { x: number; y: number }, objects: MapObject[], packs: MapPack[], bossAt?: { x: number; y: number }): ZoneMap {
  let h = 0x811c9dc5;
  for (let i = 0; i < g.tiles.length; i++) {
    h ^= g.tiles[i];
    h = Math.imul(h, 0x01000193);
  }
  return { id, w: g.w, h: g.h, tiles: g.tiles, spawn, objects, packs, bossAt, hash: h >>> 0 };
}

function placePacksAndLoot(g: Grid, rng: RNG, spawn: [number, number], exit: [number, number] | null, boss: boolean, objects: MapObject[]): MapPack[] {
  const dist = bfs(g, spawn[0], spawn[1]);
  const cells: number[] = [];
  for (let i = 0; i < dist.length; i++) if (dist[i] > 15) cells.push(i);
  rng.shuffle(cells);
  const area = cells.length;
  const packs: MapPack[] = [];
  const target = Math.floor(area / (boss ? 170 : 130));
  const minSpacing = 8;
  for (const c of cells) {
    if (packs.length >= target) break;
    const x = c % g.w;
    const y = (c / g.w) | 0;
    if (exit && boss) {
      const ex = x - exit[0];
      const ey = y - exit[1];
      if (ex * ex + ey * ey < 169) continue;
    }
    let ok = true;
    for (const p of packs) {
      if (Math.abs(p.x - x) + Math.abs(p.y - y) < minSpacing) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    const roll = rng.next();
    const tier: Tier = roll < 0.05 ? 2 : roll < 0.14 ? 1 : 0;
    packs.push({ x: x + 0.5, y: y + 0.5, tier, size: rng.int(1, 3) });
  }
  // Chests & shrines: tucked into corners away from the critical path where possible.
  const chestCount = rng.int(3, boss ? 4 : 6);
  const shrineCount = rng.int(1, 2);
  const shrineKinds = ['armor', 'combat', 'mana', 'speed', 'exp', 'gold'];
  let placed = 0;
  const used: { x: number; y: number }[] = [];
  for (let i = 0; i < cells.length && placed < chestCount + shrineCount; i++) {
    const c = cells[rng.int(0, cells.length - 1)];
    const x = c % g.w;
    const y = (c / g.w) | 0;
    if (g.get(x, y) !== T_FLOOR) continue;
    let tooClose = false;
    for (const u of used) if (Math.abs(u.x - x) + Math.abs(u.y - y) < 12) tooClose = true;
    if (tooClose) continue;
    used.push({ x, y });
    if (placed < chestCount) objects.push({ type: 'chest', x: x + 0.5, y: y + 0.5 });
    else objects.push({ type: 'shrine', x: x + 0.5, y: y + 0.5, sub: rng.pick(shrineKinds) });
    placed++;
  }
  return packs;
}

function genHub(seed: number): ZoneMap {
  const rng = new RNG(mixSeed(seed, 77));
  const w = 46;
  const h = 34;
  const g = new Grid(w, h, T_WALL);
  const cx = w >> 1;
  const cy = h >> 1;
  // Octagonal plaza
  g.rect(5, 6, w - 10, h - 12, T_FLOOR);
  g.rect(9, 3, w - 18, h - 6, T_FLOOR);
  // Corner pylons
  for (const [px, py] of [[9, 8], [w - 10, 8], [9, h - 9], [w - 10, h - 9], [cx - 8, cy - 8], [cx + 7, cy - 8], [cx - 8, cy + 7], [cx + 7, cy + 7]]) {
    g.set(px, py, T_BLOCK);
  }
  // Coolant pools - slightly different per room for flavour.
  const poolSide = rng.chance(0.5) ? -1 : 1;
  const poolX = cx + poolSide * 13;
  g.rect(poolX - 2, cy - 2, 5, 5, T_LIQUID);
  const objects: MapObject[] = [
    // Everything a new arrival needs is within one screen of the spawn point.
    { type: 'gate', x: cx + 0.5, y: cy - 4.5, sub: 'waygate' },
    { type: 'npc', x: cx - 8.5, y: cy + 1.5, sub: 'merchant' },
    { type: 'npc', x: cx + 8.5, y: cy + 1.5, sub: 'trainer' },
    { type: 'stash', x: cx - 6.5, y: cy + 4.5 },
    { type: 'portal', x: cx + 6.5, y: cy + 4.5, to: ARENA_ZONE, sub: 'arena' },
  ];
  return finish(HUB_ZONE, g, { x: cx + 0.5, y: cy + 0.5 }, objects, []);
}

function genArena(seed: number): ZoneMap {
  const rng = new RNG(mixSeed(seed, 91));
  const w = 40;
  const h = 32;
  const g = new Grid(w, h, T_WALL);
  const cx = w >> 1;
  const cy = h >> 1;
  for (let y = 2; y < h - 2; y++) {
    for (let x = 2; x < w - 2; x++) {
      const dx = (x - cx) / 17;
      const dy = (y - cy) / 13;
      if (dx * dx + dy * dy <= 1) g.set(x, y, T_FLOOR);
    }
  }
  for (const [px, py] of [[cx - 8, cy - 5], [cx + 7, cy - 5], [cx - 8, cy + 4], [cx + 7, cy + 4]]) {
    g.rect(px, py, 2, 2, T_BLOCK);
  }
  // Centre brazier pit.
  g.rect(cx - 1, cy - 1, 2, 2, T_LIQUID);
  void rng;
  const objects: MapObject[] = [{ type: 'portal', x: cx + 0.5, y: cy + 10.5, to: HUB_ZONE, sub: 'hub' }];
  return finish(ARENA_ZONE, g, { x: cx + 0.5, y: cy + 8.5 }, objects, []);
}

export function generateZoneMap(roomSeed: number, id: string): ZoneMap {
  if (id === HUB_ZONE) return genHub(roomSeed);
  if (id === ARENA_ZONE) return genArena(roomSeed);

  const info = parseZone(id);
  const realm = info.realm;
  const rng = new RNG(mixSeed(roomSeed, hashStr(id)));
  const size = info.isBossFloor ? 66 : 84;
  const g = new Grid(size, size, realm.style === 'islands' ? T_VOID : T_WALL);

  switch (realm.style) {
    case 'cave': {
      genCave(g, rng, realm.id === 'verdant' ? 0.42 : 0.44);
      addPools(g, rng, Math.floor(size / 12), realm.liquid === 'void' ? T_VOID : T_LIQUID);
      scatterBlocks(g, rng, Math.floor((size * size) / 55));
      break;
    }
    case 'rooms': {
      genRooms(g, rng, realm);
      break;
    }
    case 'open': {
      genOpen(g, rng);
      addPools(g, rng, Math.floor(size / 18), T_LIQUID);
      break;
    }
    case 'city': {
      genCity(g, rng);
      break;
    }
    case 'islands': {
      genIslands(g, rng);
      break;
    }
    default:
      throw new Error(`unhandled style ${realm.style}`);
  }

  connectRegions(g, rng, realm.style === 'islands' ? T_VOID : T_FLOOR, 12);
  let { a, b } = farPoints(g, rng);

  // Boss floors get a purpose-carved arena at the far end.
  let bossAt: { x: number; y: number } | undefined;
  if (info.isBossFloor) {
    const R = 10;
    const bx = Math.max(R + 3, Math.min(g.w - R - 4, b[0]));
    const by = Math.max(R + 3, Math.min(g.h - R - 4, b[1]));
    g.disc(bx, by, R, T_FLOOR);
    // Ring of pillars as cover
    for (const [ox, oy] of BOSS_PILLARS) g.set(bx + ox, by + oy, T_BLOCK);
    b = [bx, by];
    bossAt = { x: bx + 0.5, y: by + 0.5 };
  }

  clearAround(g, a[0], a[1], 2);
  clearAround(g, b[0], b[1], 2);
  connectRegions(g, rng, realm.style === 'islands' ? T_VOID : T_FLOOR, 12);

  const objects: MapObject[] = [];
  const up = info.floor === 0 ? HUB_ZONE : zoneId(realm.idx, info.floor - 1);
  objects.push({ type: info.floor === 0 ? 'portal' : 'stairsUp', x: a[0] + 0.5, y: a[1] + 0.5, to: up, sub: info.floor === 0 ? 'hub' : undefined });
  if (!info.isBossFloor) {
    objects.push({ type: 'stairsDown', x: b[0] + 0.5, y: b[1] + 0.5, to: zoneId(realm.idx, info.floor + 1) });
  }
  // Stand next to the entrance, on whichever adjacent tile is actually floor.
  let spawn = { x: a[0] + 0.5, y: a[1] + 0.5 };
  for (const [ox, oy] of [[0, 1], [1, 0], [-1, 0], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]]) {
    if (g.get(a[0] + ox, a[1] + oy) === T_FLOOR) {
      spawn = { x: a[0] + ox + 0.5, y: a[1] + oy + 0.5 };
      break;
    }
  }
  const packs = placePacksAndLoot(g, rng, a, b, info.isBossFloor, objects);
  return finish(id, g, spawn, objects, packs, bossAt);
}
